// base.js
// Empty application placeholder

import React, { useState, useRef } from 'react';
import { Box, Button, IconButton, Stack, Typography, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, CircularProgress, ListItemIcon, Breadcrumbs, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper as MuiPaper } from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SaveIcon from '@mui/icons-material/Save';
import api from '../services/api';
import * as XLSX from 'xlsx'; // npm install xlsx
import { backendUrl } from '../config';


export default function ExcelApp() {

  // Ouvre automatiquement un fichier Excel sélectionné via FileExplorer
  React.useEffect(() => {
    const filePath = window.localStorage.getItem('notepadApp_filePath');
    if (filePath && (filePath.endsWith('.xlsx') || filePath.endsWith('.xls'))) {
      handleExplorerSelectFile({ name: filePath.split('/').pop(), type: 'file' });
      window.localStorage.removeItem('notepadApp_filePath');
    }
  }, []);
  // État pour l'explorateur
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [explorerPath, setExplorerPath] = useState('/');
  const [explorerFiles, setExplorerFiles] = useState([]);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [explorerError, setExplorerError] = useState('');
  // État pour le document Excel
  const [excelData, setExcelData] = useState([]); // tableau de lignes
  const [excelFileName, setExcelFileName] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const tableRef = useRef();

  // --- Explorateur backend (identique PaintApp/WordApp) ---
  async function fetchExplorerFiles(path = '/') {
    setExplorerLoading(true);
    setExplorerError('');
    try {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = path === '/' ? '' : path.replace(/^\//, '');
      const res = await api.get(`/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExplorerFiles(res.data.filter(f => f.type === 'folder' || (f.type === 'file' && (f.name.endsWith('.xlsx') || f.name.endsWith('.xls')))));
      setExplorerPath(path);
    } catch (e) {
      setExplorerError("Erreur lors du chargement des fichiers.");
      setExplorerFiles([]);
    } finally {
      setExplorerLoading(false);
    }
  }

  // Ouvre un fichier Excel du backend
  async function handleExplorerSelectFile(file) {
    if (file.type !== 'file') return;
    try {
      setExplorerLoading(true);
      setExplorerError('');
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = explorerPath === '/' ? file.name : explorerPath.replace(/^\//, '') + '/' + file.name;
      // Récupère le fichier Excel en blob via /download-file
      let blob;
      try {
        // axios ne gère pas toujours bien les blobs avec FastAPI/FileResponse
        const url = `${backendUrl}/download-file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`;
        const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!resp.ok) throw new Error('Erreur lors du téléchargement du fichier Excel.');
        blob = await resp.blob();
      } catch (err) {
        setExplorerError("Impossible de télécharger le fichier Excel (réponse binaire invalide).");
        setExcelData([]);
        setExcelFileName(file.name);
        setEditMode(false);
        setExplorerOpen(false);
        return;
      }
      try {
        const arrayBuffer = await blob.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const ws = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        setExcelData(data);
      } catch (err) {
        setExplorerError("Le fichier Excel est corrompu ou illisible.");
        setExcelData([]);
      }
      setExcelFileName(file.name);
      setEditMode(false); // Désactive l'édition, mode visionneuse uniquement
      setExplorerOpen(false);
    } catch (e) {
      setExplorerError("Erreur lors de l'ouverture du fichier Excel.");
    } finally {
      setExplorerLoading(false);
    }
  }

  // Navigation
  function handleExplorerNavigate(folder) {
    const newPath = explorerPath === '/' ? `/${folder}` : `${explorerPath}/${folder}`;
    fetchExplorerFiles(newPath);
  }
  function handleExplorerGoUp() {
    if (explorerPath === '/') return;
    const parts = explorerPath.split('/').filter(Boolean);
    parts.pop();
    const newPath = parts.length === 0 ? '/' : '/' + parts.join('/');
    fetchExplorerFiles(newPath);
  }

  // Sauvegarde (tableau -> Excel -> backend)
  async function handleSave() {
    setSaveLoading(true);
    setSaveError('');
    try {
      const userStr = window.localStorage.getItem('user');
      let uuid7 = null;
      if (userStr) {
        try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
      }
      if (!uuid7) throw new Error('Utilisateur non trouvé');
      const token = window.localStorage.getItem('token');
      let relPath = explorerPath === '/' ? excelFileName : explorerPath.replace(/^\//, '') + '/' + excelFileName;
      // Convertit le tableau en fichier Excel (blob base64)
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      const arrayBuffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      const blob = new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const formData = new FormData();
      formData.append('uuid7', uuid7);
      formData.append('path', relPath.replace(/^\//, ''));
      formData.append('file', blob, excelFileName || 'document.xlsx');
      await fetch(`${backendUrl}/upload-file`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      setEditMode(false);
    } catch (e) {
      setSaveError("Erreur lors de la sauvegarde du fichier.");
    } finally {
      setSaveLoading(false);
    }
  }

  // Modification du tableau (cellule)
  function handleCellChange(rowIdx, colIdx, value) {
    setExcelData(prev => {
      const copy = prev.map(row => [...row]);
      if (!copy[rowIdx]) copy[rowIdx] = [];
      copy[rowIdx][colIdx] = value;
      return copy;
    });
  }

  // UI principale
  return (
    <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', mt: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Tooltip title="Ouvrir depuis l'explorateur">
          <IconButton color="primary" onClick={() => { setExplorerOpen(true); fetchExplorerFiles('/'); }}>
            <FolderOpenIcon />
          </IconButton>
        </Tooltip>
        {editMode && (
          <Tooltip title="Sauvegarder">
            <IconButton color="success" onClick={handleSave} disabled={saveLoading}>
              <SaveIcon />
            </IconButton>
          </Tooltip>
        )}
        {excelFileName && <Typography variant="subtitle1" sx={{ ml: 2 }}>{excelFileName}</Typography>}
      </Stack>

      {/* Explorateur de fichiers backend */}
      <Dialog open={explorerOpen} onClose={() => setExplorerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ouvrir un fichier Excel</DialogTitle>
        <DialogContent>
          <Box mb={2} display="flex" gap={2} alignItems="center">
            <Button size="small" onClick={handleExplorerGoUp} disabled={explorerPath === '/'}>Remonter</Button>
            <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: 15, flex: 1 }}>
              <span style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }} onClick={() => fetchExplorerFiles('/')}>Racine</span>
              {explorerPath.split('/').filter(Boolean).map((part, idx, arr) => {
                const path = '/' + arr.slice(0, idx + 1).join('/');
                return (
                  <span
                    key={idx}
                    style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }}
                    onClick={() => fetchExplorerFiles(path)}
                  >{part}</span>
                );
              })}
            </Breadcrumbs>
          </Box>
          <Box sx={{ border: '2px solid #b3c6e0', borderRadius: 2, bgcolor: '#fafdff', p: 1, boxShadow: 1, maxHeight: 270, overflowY: 'auto', mt: 1, mb: 2 }}>
            {explorerLoading ? <CircularProgress /> : (
              <List sx={{ fontSize: 17 }}>
                {explorerFiles.map((file, idx) => (
                  <ListItem
                    button={file.type === 'folder' || (file.type === 'file' && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')))}
                    key={idx}
                    onDoubleClick={file.type === 'folder' ? () => handleExplorerNavigate(file.name) : undefined}
                    onClick={file.type === 'file' && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) ? () => handleExplorerSelectFile(file) : undefined}
                    sx={{ py: 2, borderRadius: 2, mb: 1, '&:hover': { bgcolor: file.type === 'folder' ? '#e3f2fd' : undefined } }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {file.type === 'folder' ? <FolderIcon color="primary" fontSize="medium" /> : <InsertDriveFileIcon fontSize="medium" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={<span style={{ fontSize: 17, fontWeight: file.type === 'folder' ? 600 : 400, color: file.type === 'folder' ? '#1976d2' : '#222' }}>{file.name}</span>}
                      secondary={file.type === 'folder' ? 'Dossier' : 'Fichier'}
                      style={file.type === 'folder' ? { cursor: 'pointer', color: '#1976d2' } : {}}
                    />
                  </ListItem>
                ))}
                {(!explorerFiles || explorerFiles.length === 0) && <ListItem><ListItemText primary={<span style={{ fontSize: 16 }}>Dossier vide</span>} /></ListItem>}
              </List>
            )}
            {explorerError && <Typography color="error" sx={{ mt: 2, fontSize: 16 }}>{explorerError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExplorerOpen(false)} size="large">Annuler</Button>
        </DialogActions>
      </Dialog>

      {/* Zone de visionneuse Excel */}
      {excelData && excelData.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Fichier Excel ouvert : {excelFileName}
          </Typography>
          <TableContainer component={MuiPaper}>
            <Table ref={tableRef} size="small">
              <TableHead>
                <TableRow>
                  {excelData[0] && excelData[0].map((col, idx) => (
                    <TableCell key={idx} sx={{ fontWeight: 600, bgcolor: '#e3f2fd' }}>{col || ''}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {excelData.slice(1).map((row, rowIdx) => (
                  <TableRow key={rowIdx}>
                    {excelData[0] && excelData[0].map((col, colIdx) => (
                      <TableCell key={colIdx} sx={{ minWidth: 80 }}>
                        {row[colIdx] || ''}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}